import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Hidden
} from '@material-ui/core';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import { deepOrange, blueGrey, lightBlue, green, yellow } from '@material-ui/core/colors';
import { SortingColumn } from '../../../../components';
import { toCurrency, toDate } from '../../../../helpers/format';

const useStyles = makeStyles(theme => ({
  root: { },
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1050
  },
  actions: {
    justifyContent: 'flex-end'
  },
  view: {
      width: 140
  },
  EP: {
    color: theme.palette.getContrastText(lightBlue[500]),
    backgroundColor: lightBlue[500],
  },
  C: {
    color: theme.palette.getContrastText(blueGrey[500]),
    backgroundColor: blueGrey[500],
  },
  FP: {
    color: "#000",
    backgroundColor: yellow[500],
  },
  P: {
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
  },
  E: {
    color: '#fff',
    backgroundColor: green[500],
  }
}));

const OrderTable = props => {
  const { className, orders, onViewOrder, page, setPage, ...rest } = props;

  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState('reference');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    setPage(0);
    setRowsPerPage(event.target.value);
  };
  const onSorting = (newSort, newSortDirection) => {
    setSort(newSort);
    setSortDirection(newSortDirection);
  };

  const sortedOrders = () => {
    return orders.sort((a, b) => {
      const aValue = sort.split('.').reduce((c, d) => c[d], a);
      const bValue = sort.split('.').reduce((c, d) => c[d], b);
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      return 0;
    });
  };

  const orderClassName = (statusDescription) => {
    const description = statusDescription ? statusDescription.toLowerCase() : "";
    switch (description) {
      case "en proceso":
        return "EP";
      case "cumplida":
        return "C";
      case "pendiente - falta pago":
      case "cumplida - falta pago":
        return "FP";
      case "pendiente":
        return "P";
      case "entregada (cerrada)":
        return "E";
      default:
        return "";
    }
  }

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table>
              <TableHead>
                <TableRow>
                <SortingColumn sort={"reference"} label={"# Referencia"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"date"} label={"Fecha"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"code"} label={"Código"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"description"} label={"Descripción"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"price"} label={"Precio"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"qty"} label={"Cantidad"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"unit"} label={"UDM"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"status"} label={"Estado"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <TableCell className={classes.view}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedOrders().slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage).map(order => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={order.id}
                  >
                    <TableCell>
                        {order.reference}
                    </TableCell>
                    <TableCell>{toDate(order.date)}</TableCell>
                    <TableCell>
                        {order.code}
                    </TableCell>
                    <TableCell>{order.description}</TableCell>
                    <TableCell>
                      {toCurrency(order.price)}
                    </TableCell>
                    <TableCell>{order.qty}</TableCell>
                    <TableCell>{order.unit}</TableCell>
                    <TableCell>
                      <Tooltip title={order.status_description} placement="top-start">
                        <Avatar className={classes[orderClassName(order.status_description)]}>{order.status}</Avatar>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                    <Button color="primary" variant="contained" onClick={() => onViewOrder(order.order_id)}>
                      <Hidden smUp><RemoveRedEyeIcon /></Hidden>
                      <Hidden xsDown>Ver</Hidden>
                    </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={orders.length}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </CardActions>
    </Card>
  );
};

OrderTable.propTypes = {
  className: PropTypes.string,
  orders: PropTypes.array.isRequired
};

export default OrderTable;
